/**
 * Resolves the assignee id that should count for completion-status purposes:
 * the canonical assignee on the owning vendor/employee entity (`vendorsMap`/
 * `importedVendorsMap`), falling back to the row's own `assigneeId` field
 * when the entity can't be found. For employees, a row's own `assigneeId`
 * is joined server-side from `BaseAssignment` and can disagree with (or, for
 * stale duplicate `BaseAssignment` rows, fail to match at all) the canonical
 * assignee on the `Employee` entity itself -- see
 * `TimePeriodTable.vue`'s `employeeCanonicalAssigneeId`, which this mirrors.
 * @param {Object} item
 * @param {{vendorsMap?: Object, importedVendorsMap?: Object}} [maps]
 * @returns {number|string|null|undefined}
 */
export const resolveCanonicalAssigneeId = (
   item,
   {vendorsMap = {}, importedVendorsMap = {}} = {}
) => {
   const id = getManualVendorId(item);
   const vendor = vendorsMap[id] ?? importedVendorsMap[id];
   return vendor ? vendor.assigneeId : item.assigneeId;
};

const sortAssigneeIds = (ids) =>
   [...ids].sort((a, b) => String(a ?? '').localeCompare(String(b ?? '')));

/**
 * Returns the normalized id used for manual vendor/ad-hoc rows.
 * @param {Object} [item={}]
 * @returns {number|string|null|undefined}
 */
export const getManualVendorId = (item = {}) =>
   item.contractorId ?? item.vendorId ?? item.employeeId;

/**
 * Builds `/summary/set-zero` request payload from summary identity.
 * Returns null when company, period, or vendor identity is missing.
 * @param {{
 *   summary: Object,
 *   companyId?: number|string|null|undefined,
 *   fallbackCompanyId?: number|string|null|undefined
 * }} params
 * @returns {{
 *   companyId: number|string,
 *   period: Object,
 *   names: Array<{account: string, vendor: string, location: string|null}>,
 *   ids: Array<number|string>,
 *   assigneeId: number|string|null|undefined
 * }|null}
 */
export const buildSetZeroPayload = ({summary, companyId, fallbackCompanyId}) => {
   const resolvedCompanyId = companyId ?? fallbackCompanyId;
   const period = summary?.period?.period;
   const manualVendorId = getManualVendorId(summary);
   const hasManualVendorIdentity = manualVendorId !== null && manualVendorId !== undefined;
   const hasImportedVendorIdentity = !!summary?.account && !!summary?.vendor;

   if (
      resolvedCompanyId === null ||
      resolvedCompanyId === undefined ||
      !period ||
      (!hasManualVendorIdentity && !hasImportedVendorIdentity)
   ) {
      return null;
   }

   return {
      companyId: resolvedCompanyId,
      period,
      names: hasImportedVendorIdentity
         ? [
              {
                 account: summary.account,
                 vendor: summary.vendor,
                 location: summary.location,
              },
           ]
         : [],
      ids: hasManualVendorIdentity ? [manualVendorId] : [],
      assigneeId: summary.assigneeId,
   };
};

const normalizeComparableLocation = (location) => location ?? null;

/**
 * Compares two location values while treating null/undefined as equivalent.
 * @param {*} left
 * @param {*} right
 * @returns {boolean}
 */
export const locationsMatch = (left, right) =>
   normalizeComparableLocation(left) === normalizeComparableLocation(right);

/**
 * Finds a summary row in state by ad-hoc identity, account/vendor identity, or summary id.
 * @param {{accountData: Object<string, Object<string, Array<Object>>>, adHocData: Object<string, Array<Object>>}} stateData
 * @param {Object} summary
 * @returns {Object|null}
 */
export const findSummaryRowInState = ({accountData, adHocData}, summary) => {
   if (!summary) return null;

   const periodId = summary.period?.id;
   if (!periodId) return null;

   const summaryLocation = summary.location;
   const summaryId = summary.id;
   const accountRowsByPeriod = accountData[periodId] || {};
   const adHocRowsByPeriod = adHocData[periodId] || [];

   const isAdHocSummary = summary.account === null || summary.account === undefined;
   const targetManualId = summary.contractorId ?? summary.vendorId;
   if (isAdHocSummary && targetManualId !== null && targetManualId !== undefined) {
      const adHocMatch = adHocRowsByPeriod.find(
         (row) =>
            String(getManualVendorId(row)) === String(targetManualId) &&
            locationsMatch(row.location, summaryLocation)
      );
      if (adHocMatch) return adHocMatch;
   }

   if (summary.account && summary.vendor) {
      const accountRows = accountRowsByPeriod[summary.account] || [];
      const accountMatch = accountRows.find(
         (row) => row.vendor === summary.vendor && locationsMatch(row.location, summaryLocation)
      );
      if (accountMatch) return accountMatch;
   }

   if (summaryId !== null && summaryId !== undefined) {
      const adHocById = adHocRowsByPeriod.find((row) => row.id === summaryId);
      if (adHocById) return adHocById;

      for (const rows of Object.values(accountRowsByPeriod)) {
         const accountById = rows.find((row) => row.id === summaryId);
         if (accountById) return accountById;
      }
   }

   if (summary.vendor) {
      for (const rows of Object.values(accountRowsByPeriod)) {
         const accountFallback = rows.find(
            (row) => row.vendor === summary.vendor && locationsMatch(row.location, summaryLocation)
         );
         if (accountFallback) return accountFallback;
      }
   }

   return null;
};

/**
 * Finds an imported-vendor summary row by account/vendor/location.
 * @returns {{rows: Array<Object>, index: number}}
 */
export const findImportedVendorRowIndex = ({accountData, periodId, account, vendor, location}) => {
   const rows = accountData?.[periodId]?.[account] || [];
   const index = rows.findIndex((r) => r.vendor === vendor && locationsMatch(r.location, location));
   return {rows, index};
};

/**
 * Finds a manual/ad-hoc summary row by vendorId/contractorId and location.
 * @returns {{rows: Array<Object>, index: number}}
 */
export const findManualVendorRowIndex = ({
   adHocData,
   periodId,
   vendorId,
   contractorId,
   location,
}) => {
   const targetId = contractorId ?? vendorId;
   const rows = adHocData?.[periodId] || [];
   const index = rows.findIndex(
      (r) =>
         String(getManualVendorId(r)) === String(targetId) && locationsMatch(r.location, location)
   );
   return {rows, index};
};

/**
 * Builds a deduplication key for in-flight save requests.
 * @param {{summaryId?: number|string|null, field: string, projectId?: number|string|null}} params
 * @returns {string}
 */
export const buildSaveRequestKey = ({summaryId, field, projectId = null}) =>
   `${summaryId ?? 'missing'}:${field}:${projectId ?? ''}`;

/**
 * Creates a shallow clone of projectsData with an empty-object fallback.
 * @param {Object<string, *>|null|undefined} projectsData
 * @returns {Object<string, *>}
 */
export const cloneProjectsData = (projectsData) => ({...(projectsData || {})});

/**
 * Creates a shallow clone of activitiesData with an empty-object fallback.
 * @param {Object<string, *>|null|undefined} activitiesData
 * @returns {Object<string, *>}
 */
export const cloneActivitiesData = (activitiesData) => ({...(activitiesData || {})});

/**
 * Takes a rollback snapshot of a summary row's mutable fields.
 * @param {{accountData: Object<string, Object<string, Array<Object>>>, adHocData: Object<string, Array<Object>>}} state
 * @param {Object} summary
 * @returns {{
 *   percentage: number|null,
 *   projectsData: Object<string, *>,
 *   activitiesData: Object<string, *>,
 *   totalDebit: number|null,
 *   assignable: boolean,
 *   totalCredit: number
 * }|null}
 */
export const snapshotSummaryRow = (state, summary) => {
   const row = findSummaryRowInState(state, summary);
   if (!row) return null;

   return {
      percentage: row?.percentage?.value ?? null,
      projectsData: cloneProjectsData(row.projectsData),
      activitiesData: cloneActivitiesData(row.activitiesData),
      totalDebit: row.totalDebit ?? null,
      assignable: row.assignable ?? false,
      totalCredit: row.totalCredit ?? 0,
   };
};

/**
 * Builds the ordered set of assignees visible for the current user context.
 * @param {{importedVendors: Array<Object>, manualVendors: Array<Object>, isSME: boolean, profileId: number|string|null|undefined}} params
 * @returns {Array<number|string|null>}
 */
export const getVisibleAssignedUsers = ({importedVendors, manualVendors, isSME, profileId}) => {
   const ids = new Set();
   [...importedVendors, ...manualVendors].forEach(({assigneeId}) => ids.add(assigneeId || null));

   if (isSME) {
      return ids.has(profileId) ? [profileId] : [];
   }

   if (ids.has(profileId)) {
      ids.delete(profileId);
      return [profileId, ...sortAssigneeIds(ids)];
   }

   return sortAssigneeIds(ids);
};
